import { Portal } from "@radix-ui/react-portal";
import { X } from "@phosphor-icons/react";
import { CreateNewStipend } from "./CreateNewStipend";
import { useEffect, useRef, useState } from "react";
import { SelectTemplate } from "./SelectTemplate";
import { BaseForm } from "./BaseForm";
import { DetailsForm } from "./DetailsForm";
import { RulePolicy } from "./RulePolicy";
import { Confirmation } from "./Confirmation";
import { CreateStipendStep } from "./types";
import FeedbackDialog from "@common/components/FeedbackDialog";
import { StepperLegend } from "./StepperLegend";
import { animated, useTransition } from "@react-spring/web";
import { useRecoilValue } from "recoil";
import { createOrEditStipendState } from "@common/state/createOrEditStipendState";

const StepComponents = {
  [CreateStipendStep.CreateNewStipend]: CreateNewStipend,
  [CreateStipendStep.SelectTemplate]: SelectTemplate,
  [CreateStipendStep.BaseForm]: BaseForm,
  [CreateStipendStep.DetailsForm]: DetailsForm,
  [CreateStipendStep.RulePolicy]: RulePolicy,
  [CreateStipendStep.Confirmation]: Confirmation,
};

const StepsWithFeedback = [
  CreateStipendStep.SelectTemplate,
  CreateStipendStep.DetailsForm,
  CreateStipendStep.RulePolicy,
  CreateStipendStep.BaseForm,
  CreateStipendStep.Confirmation,
];

const StepsWithLegend = [
  CreateStipendStep.BaseForm,
  CreateStipendStep.DetailsForm,
  CreateStipendStep.RulePolicy,
  CreateStipendStep.Confirmation,
];

export const CreateStipendSheet = ({ onClose }: { onClose: () => void }) => {
  const { id } = useRecoilValue(createOrEditStipendState);
  const isEditing = !!id;
  const [path, setPath] = useState<CreateStipendStep[]>([
    isEditing ? CreateStipendStep.BaseForm : CreateStipendStep.CreateNewStipend,
  ]);
  const currentStep = path[path.length - 1];
  const prevPath = useRef(path);

  const isNavingatingToPrevStep = path.length < prevPath.current.length;
  const isLegendMounting =
    path.length > prevPath.current.length &&
    currentStep === CreateStipendStep.BaseForm;
  const transition = useTransition(currentStep, {
    keys: null,
    from: {
      opacity: 0,
      legendOpacity: isLegendMounting ? 0 : 1,
      transform: `translateX(${isNavingatingToPrevStep ? "-" : ""}100%)`,
    },
    enter: {
      opacity: 1,
      transform: "translateX(0%)",
      legendOpacity: 1,
    },
  });

  useEffect(() => {
    prevPath.current = path;
  });

  const pushStep = (step?: CreateStipendStep) => {
    if (!step) {
      onClose();
      return;
    }
    setPath((prev) => [...prev, step]);
  };
  const onBack = () => {
    if (path.length === 1) {
      onClose();
    } else {
      setPath((prev) => prev.slice(0, -1));
    }
  };

  return (
    <Portal>
      <div className="fixed inset-0 z-50 flex h-screen bg-black">
        {transition(
          (style, item) =>
            StepsWithLegend.includes(item) && (
              <animated.div
                className="flex shrink-0 flex-col justify-center pl-6 lg:pl-14"
                style={{ opacity: style.legendOpacity }}
              >
                <StepperLegend step={currentStep} />
              </animated.div>
            ),
        )}
        <div className="flex h-full grow flex-col overflow-y-auto overflow-x-hidden pb-12 pr-6 pt-9 scrollbar-thin lg:pr-14">
          <div className="flex h-10 items-center justify-end gap-x-8">
            {StepsWithFeedback.includes(currentStep) && <FeedbackDialog />}
            <X className="size-6" onClick={onClose} />
          </div>
          {transition((style, item: CreateStipendStep) => {
            const StepComponent = StepComponents[item];
            return (
              <StepComponent
                style={style}
                onBack={onBack}
                onNext={pushStep}
                isEditing={isEditing}
              />
            );
          })}
        </div>
      </div>
    </Portal>
  );
};
